import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Meta from '../components/Meta';
import useLocalePath from '../i18n/useLocalePath';

const SUPPORT_EMAIL = 'help@nekochan.app';
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

/* /data-deletion is declared separately in the Play Console data-safety form.
   It resolves to the same page, staying inside the visitor's language tree. */
export function DataDeletionRedirect() {
  const { lp } = useLocalePath();
  return <Navigate to={lp('/delete-account')} replace />;
}

function StepCard({ number, title, body }) {
  return (
    <div className="flex gap-4">
      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-brand text-white font-rubik font-bold text-bodyS flex items-center justify-center">
        {number}
      </span>
      <div>
        <h3 className="font-rubik font-semibold text-h5 text-gray-900">{title}</h3>
        <p className="font-rubik text-body text-gray-600 mt-1">{body}</p>
      </div>
    </div>
  );
}

function ScopeList({ heading, items, tone }) {
  const isKept = tone === 'kept';
  return (
    <div
      className={`rounded-2xl p-6 border ${
        isKept ? 'border-gray-200 bg-gray-50' : 'border-brand/25 bg-brand/5'
      }`}
    >
      <h3 className="font-rubik font-bold text-h5 text-gray-900 mb-3">{heading}</h3>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item} className="flex gap-2 font-rubik text-bodyS text-gray-700">
            <span className={isKept ? 'text-gray-400' : 'text-brand'} aria-hidden="true">
              {isKept ? '•' : '✓'}
            </span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RequestForm() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState('idle');

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus('sending');

    const form = new FormData(event.target);
    form.append('access_key', WEB3FORMS_KEY);
    form.append('subject', 'Data Deletion Request — nekochan.app');
    form.append('from_name', 'Nekochan deletion form');
    form.append('locale', i18n.language);

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: form,
      });
      setStatus(res.ok ? 'sent' : 'error');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <div className="rounded-2xl border border-brand/30 bg-brand/5 p-8 text-center">
        <span className="text-4xl" role="img" aria-hidden="true">🎉</span>
        <h3 className="font-rubik font-bold text-h4 text-gray-900 mt-3">
          {t('deleteAccount.form.successTitle')}
        </h3>
        <p className="font-rubik text-body text-gray-600 mt-2">
          {t('deleteAccount.form.successBody')}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Web3Forms honeypot — bots fill it, humans never see it */}
      <input type="checkbox" name="botcheck" className="hidden" tabIndex="-1" autoComplete="off" />

      <div>
        <label htmlFor="del-email" className="block font-rubik font-medium text-bodyS text-gray-800 mb-1">
          {t('deleteAccount.form.emailLabel')}
        </label>
        <input
          id="del-email"
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder={t('deleteAccount.form.emailPlaceholder')}
          className="w-full rounded-xl border-gray-300 font-rubik text-body focus:border-brand focus:ring-brand"
        />
        <p className="font-rubik text-small text-gray-500 mt-1">
          {t('deleteAccount.form.emailHint')}
        </p>
      </div>

      <div>
        <label htmlFor="del-request" className="block font-rubik font-medium text-bodyS text-gray-800 mb-1">
          {t('deleteAccount.form.requestLabel')}
        </label>
        <select
          id="del-request"
          name="request_type"
          className="w-full rounded-xl border-gray-300 font-rubik text-body focus:border-brand focus:ring-brand"
        >
          <option>{t('deleteAccount.form.requestOptions.account')}</option>
          <option>{t('deleteAccount.form.requestOptions.progress')}</option>
          <option>{t('deleteAccount.form.requestOptions.other')}</option>
        </select>
      </div>

      <div>
        <label htmlFor="del-details" className="block font-rubik font-medium text-bodyS text-gray-800 mb-1">
          {t('deleteAccount.form.detailsLabel')}
        </label>
        <textarea
          id="del-details"
          name="details"
          rows="4"
          placeholder={t('deleteAccount.form.detailsPlaceholder')}
          className="w-full rounded-xl border-gray-300 font-rubik text-body focus:border-brand focus:ring-brand"
        />
      </div>

      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full rounded-xl bg-brand hover:bg-[#0fa8a8] disabled:opacity-60 text-white font-rubik font-semibold text-body py-3 transition-colors"
      >
        {status === 'sending' ? t('deleteAccount.form.sending') : t('deleteAccount.form.submit')}
      </button>

      {status === 'error' && (
        <p className="font-rubik text-bodyS text-red text-center">
          {t('deleteAccount.form.error', { email: SUPPORT_EMAIL })}
        </p>
      )}
    </form>
  );
}

export default function DeleteAccount() {
  const { t } = useTranslation();

  const steps = t('deleteAccount.steps', { returnObjects: true });
  const deleted = t('deleteAccount.scope.deletedItems', { returnObjects: true });
  const kept = t('deleteAccount.scope.keptItems', { returnObjects: true });

  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    'Data Deletion Request'
  )}&body=${encodeURIComponent(t('deleteAccount.mailtoBody'))}`;

  return (
    <div className="bg-white">
      <Meta titleKey="deleteAccount.meta.title" descKey="deleteAccount.meta.description" />

      <div className="max-w-3xl mx-auto px-4 py-12 md:py-16">
        {/* ── HERO ── */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <h1 className="font-rubik font-bold text-h2 md:text-h1 text-gray-900 mb-4">
            {t('deleteAccount.heading')}
          </h1>
          <p className="font-rubik text-body text-gray-600">
            {t('deleteAccount.intro', { appName: 'Nekochan' })}
          </p>
        </motion.header>

        {/* ── OPTION 1: IN-APP (the real path) ── */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-rubik font-bold text-h3 text-gray-900">
              {t('deleteAccount.inApp.heading')}
            </h2>
            <span className="rounded-full bg-brand/15 text-[#0a8f8f] font-rubik font-semibold text-small px-3 py-1">
              {t('deleteAccount.inApp.badge')}
            </span>
          </div>
          <p className="font-rubik text-body text-gray-600 mb-6">
            {t('deleteAccount.inApp.intro')}
          </p>

          <div className="rounded-2xl border border-gray-200 p-6 md:p-8 space-y-6">
            {steps.map((step, i) => (
              <StepCard key={step.title} number={i + 1} title={step.title} body={step.body} />
            ))}
          </div>
        </section>

        {/* ── OPTION 2: REQUEST BY FORM / EMAIL ── */}
        <section className="mb-12">
          <h2 className="font-rubik font-bold text-h3 text-gray-900 mb-1">
            {t('deleteAccount.request.heading')}
          </h2>
          <p className="font-rubik text-body text-gray-600 mb-6">
            {t('deleteAccount.request.intro')}
          </p>

          <div className="rounded-2xl border border-gray-200 p-6 md:p-8">
            {WEB3FORMS_KEY ? (
              <RequestForm />
            ) : (
              <p className="font-rubik text-body text-gray-600 mb-4">
                {t('deleteAccount.request.emailOnly')}
              </p>
            )}

            <p className="font-rubik text-bodyS text-gray-500 mt-6 pt-6 border-t border-gray-200">
              {t('deleteAccount.request.mailtoPrefix')}{' '}
              <a href={mailto} className="text-[#0a8f8f] font-medium hover:underline">
                {SUPPORT_EMAIL}
              </a>
              {t('deleteAccount.request.mailtoSuffix')}
            </p>
          </div>
        </section>

        {/* ── WHAT GETS DELETED ── */}
        <section className="mb-12">
          <h2 className="font-rubik font-bold text-h3 text-gray-900 mb-6">
            {t('deleteAccount.scope.heading')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ScopeList heading={t('deleteAccount.scope.deletedHeading')} items={deleted} tone="deleted" />
            <ScopeList heading={t('deleteAccount.scope.keptHeading')} items={kept} tone="kept" />
          </div>
        </section>

        {/* ── TIMELINE + SUBSCRIPTION WARNING ── */}
        <section className="space-y-4">
          <div className="rounded-2xl bg-gradient-to-r from-brand to-[#0fa8a8] text-white p-6 md:p-8">
            <h2 className="font-rubik font-bold text-h4 mb-2">
              {t('deleteAccount.timeline.heading')}
            </h2>
            <p className="font-rubik text-body text-white/90">
              {t('deleteAccount.timeline.body')}
            </p>
          </div>

          <div className="rounded-2xl border border-yellow/40 bg-yellow/10 p-6">
            <h2 className="font-rubik font-bold text-h5 text-gray-900 mb-2">
              ⚠️ {t('deleteAccount.subscription.heading')}
            </h2>
            <p className="font-rubik text-bodyS text-gray-700">
              {t('deleteAccount.subscription.body')}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
